import React, { useState, useEffect } from "react";
import axios from "axios";
import { collection, getDocs, doc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import "../styles/admin.css";

const AdminDashboard = () => {
  const { currentUser, userProfile, loading } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("products");

  const [formData, setFormData] = useState({
    name: "",
    price: "",
    description: "",
    deliveryDuration: "",
    packingMethod: "",
    shippingFee: "",
    category: "",
    stock: "",
    images: []
  });

  const [message, setMessage] = useState("");
  const [orders, setOrders] = useState([]);

  /* ---------------- ADMIN AUTH CHECK ---------------- */

  useEffect(() => {
    if (loading) return;

    if (!currentUser) {
      navigate("/login");
    } else if (userProfile !== null && !userProfile.isAdmin) {
      alert("You are not an admin.");
      navigate("/");
    }
  }, [currentUser, userProfile, loading, navigate]);

  /* ---------------- HANDLE INPUT ---------------- */

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleImageChange = (e) => {
    setFormData({
      ...formData,
      images: e.target.files
    });
  };

  /* ---------------- ADD PRODUCT ---------------- */

  const handleSubmit = async (e) => {
    e.preventDefault();

    const form = new FormData();

    form.append("name", formData.name);
    form.append("price", Number(formData.price));
    form.append("description", formData.description);
    form.append("deliveryDuration", formData.deliveryDuration);
    form.append("packingMethod", formData.packingMethod);
    form.append("shippingFee", formData.shippingFee);
    form.append("category", formData.category);
    form.append("stock", Number(formData.stock));

    for (let i = 0; i < formData.images.length; i++) {
      form.append("images", formData.images[i]);
    }

    try {
      await axios.post(
        "https://ecommerce-backend-zxtw.onrender.com/api/products",
        form,
        {
          headers: {
            "Content-Type": "multipart/form-data"
          }
        }
      );

      setMessage("✅ Product Added Successfully!");

      setFormData({
        name: "",
        price: "",
        description: "",
        deliveryDuration: "",
        packingMethod: "",
        shippingFee: "",
        category: "",
        stock: "",
        images: []
      });

    } catch (error) {
      console.error(error);
      setMessage("❌ Error Adding Product");
    }
  };

  /* ---------------- FETCH ORDERS ---------------- */

  const fetchOrders = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, "orders"));

      const orderList = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      }));

      setOrders(orderList);
    } catch (error) {
      console.error("Error fetching orders", error);
    }
  };

  useEffect(() => {
    if (activeTab === "orders") {
      fetchOrders();
    }
  }, [activeTab]);

  /* ---------------- UPDATE ORDER STATUS ---------------- */

  const updateStatus = async (id, status) => {
    try {
      const orderRef = doc(db, "orders", id);

      await updateDoc(orderRef, { status });

      fetchOrders();
    } catch (error) {
      console.error("Error updating status", error);
    }
  };

  if (loading) {
    return (
      <div className="center-loader">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="admin-container">

      <Navbar />

      <div className="admin-tabs">
        <button
          className={activeTab === "products" ? "active-tab" : ""}
          onClick={() => setActiveTab("products")}
        >
          Add Product
        </button>

        <button
          className={activeTab === "orders" ? "active-tab" : ""}
          onClick={() => setActiveTab("orders")}
        >
          Orders
        </button>
      </div>

      {/* ---------------- ADD PRODUCT ---------------- */}

      {activeTab === "products" && (
        <div className="admin-card">

          <h2 className="admin-title">Add New Product</h2>

          <form onSubmit={handleSubmit} className="admin-form">

            <input
              type="text"
              name="name"
              placeholder="Product Name"
              value={formData.name}
              onChange={handleChange}
              required
            />

            <input
              type="number"
              name="price"
              placeholder="Price"
              value={formData.price}
              onChange={handleChange}
              required
            />

            <textarea
              name="description"
              placeholder="Product Description"
              value={formData.description}
              onChange={handleChange}
            />

            <input
              type="text"
              name="deliveryDuration"
              placeholder="Delivery Duration"
              value={formData.deliveryDuration}
              onChange={handleChange}
            />

            <input
              type="text"
              name="packingMethod"
              placeholder="Packing Method"
              value={formData.packingMethod}
              onChange={handleChange}
            />

            <input
              type="text"
              name="shippingFee"
              placeholder="Shipping Fee"
              value={formData.shippingFee}
              onChange={handleChange}
            />

            {/* ✅ CATEGORY DROPDOWN */}
            <select
              name="category"
              value={formData.category}
              onChange={handleChange}
              required
            >
              <option value="">Select Category</option>
              <option value="Streetwear">Streetwear</option>
              <option value="Casual">Casual</option>
              <option value="Formal">Formal</option>
              <option value="Oversized">Oversized</option>
              <option value="Minimal">Minimal</option>
              <option value="Luxury">Luxury</option>
            </select>

            <input
              type="number"
              name="stock"
              placeholder="Stock Quantity"
              value={formData.stock}
              onChange={handleChange}
            />

            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handleImageChange}
              required
            />

            <button type="submit" className="admin-btn">
              Add Product
            </button>

          </form>

          <p className="admin-message">{message}</p>

        </div>
      )}

      {/* ---------------- ORDERS ---------------- */}

      {activeTab === "orders" && (
        <div className="orders-container">

          <h2>Customer Orders</h2>

          <table className="orders-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Phone</th>
                <th>Pincode</th>
                <th>Total</th>
                <th>Status</th>
                <th>Update</th>
              </tr>
            </thead>

            <tbody>
              {orders.map((order) => (
                <tr key={order.id}>

                  <td>{order.user?.name || "N/A"}</td>
                  <td>{order.user?.phone || "N/A"}</td>
                  <td>{order.user?.pincode || "N/A"}</td>
                  <td>₹{order.total}</td>
                  <td>{order.status}</td>

                  <td>
                    <select
                      onChange={(e) =>
                        updateStatus(order.id, e.target.value)
                      }
                      defaultValue={order.status}
                    >
                      <option value="paid">Paid</option>
                      <option value="pending">Pending</option>
                      <option value="Processing">Processing</option>
                      <option value="Shipped">Shipped</option>
                      <option value="Delivered">Delivered</option>
                      <option value="Cancelled">Cancelled</option>
                    </select>
                  </td>

                </tr>
              ))}
            </tbody>
          </table>

        </div>
      )}

    </div>
  );
};

export default AdminDashboard;